import ApiError from './ApiError'
import autoBind from 'auto-bind'
import dayjs from "dayjs";

import { mapJobTypeToPosition } from 'utils'
import { Location } from 'components/BillingLocationSelector'
import { BackendJob } from 'components/EditJob'
import { Application } from 'components/JobBoardDetails'
import { NewLocation } from 'components/AddOfficeLocation'
import { SavedCard } from "components/PurchaseTokensFlow";
import { CandidateStats } from '.'
import { EditedJob, formatEditJobBackend, HygienistBackendJob } from 'components/EditJob'
import * as ls from "local-storage";
import {InvoiceProps, PositionProps, Type, UserAccountProps, SkillsProps, HygienistScoreProps, BasicHygienistScoreProps} from "utils/types";
import {JobDataProps} from "../pages/dentist/PostJobPage/PostJob";
import { HygienistSkill } from 'utils/constants';

// --- Added Talent Review Types ---
interface ReviewData {
	jobId: number;
	locationId: number;
	collaborationPreference: 1 | 2 | 3; // 1: Favorite, 2: Neutral, 3: Do Not Work Here
	friendlinessRating?: number | null;
	infectionControlRating?: number | null;
	equipmentRating?: number | null;
	preparationRating?: number | null;
	comments?: string;
}

interface TalentReview {
	id: number;
	jobId: number;
	hygienistId: number;
	locationId: number;
	friendlinessRating: 1 | 2 | 3 | 4 | 5 | null;
	infectionControlRating: 1 | 2 | 3 | 4 | 5 | null;
	equipmentRating: 1 | 2 | 3 | 4 | 5 | null;
	preparationRating: 1 | 2 | 3 | 4 | 5 | null;
	collaborationPreference: CollaborationPreference | null;
	comments: string | null;
	reviewDate: Date; // Assuming backend sends a string convertible to Date
	submissionStatus: SubmissionStatus | null;
}

enum SubmissionStatus {
	Deferred = 0,
	Submitted = 1,
	Dismissed = 2
}

enum CollaborationPreference {
	WOULD_LOVE_TO_WORK_AGAIN = 1,
	FINE_BUT_NOT_FAVOURITE = 2,
	PREFER_NOT_TO_WORK_AGAIN = 3
}

interface PendingReview {
	jobId: number;
	locationId: number;
	startDate: string;
	endDate: string;
	officeName: string;
}
// --- End Added Talent Review Types ---

export default class Api {
	baseUrl: string
	commonHeaders: { [key: string]: string }
	authorizationHeader: string

	constructor(baseUrl: string, authorizationHeader: string) {
		this.baseUrl = baseUrl
		this.authorizationHeader = authorizationHeader

		this.commonHeaders = {
			Accept: 'application/json',
			'content-type': 'application/json',
		}

		autoBind(this)
	}

	private async request(url: string, options: RequestInit = {}) {
		const res = await fetch(`${this.baseUrl}${url}`, {
			credentials: 'include',
			mode: "cors",
			headers: {
				...this.commonHeaders,
				'Authorization': this.authorizationHeader
			},
			...options,
		})
		if (
			res.status.toString().startsWith('4') ||
			res.status.toString().startsWith('5')
		) {
			throw new ApiError(
				res.status,
				await res.text(),
			)
		}
		if (res.status === 204) {
			return res
		}
		return res.json()
	}

	private get(url: string) {
		return this.request(url, {
			method: 'GET',
		})
	}

	private delete(url: string, body?: object) {
		return this.request(url, {
			method: 'DELETE',
			body: body
				? JSON.stringify(body)
				: undefined,
		})
	}

	private post(url: string, body?: object) {
		return this.request(url, {
			method: 'POST',
			body: body
				? JSON.stringify(body)
				: undefined,
		})
	}

	private put(url: string, body?: object) {
		return this.request(url, {
			method: 'PUT',
			body: body
				? JSON.stringify(body)
				: undefined,
		})
	}

	async updateToken() {
		const token = ls.get('userAuth') as {jwtToken: string};
		this.authorizationHeader = `Bearer ${token.jwtToken}`
	}

	async getAllHireJobs(): Promise<Array<BackendJob>> {
		const backendHireJobs: Array<any> = await this.get('v3/hireJobs')

		return backendHireJobs
			.map((
				{
					jobType,
					new_applications,
					status,
					...props
				},
			) => ({
				position: mapJobTypeToPosition(jobType),
				jobType,
				newCandidates: new_applications,
				status,
				...props,
			}))
	}

	getAvailableHireJobs(): Promise<Array<HygienistBackendJob>> {
		return this.get('v3/hireJobs')
	}

	async getPublicHireJobByIdForHygienist(jobId: number): Promise<HygienistBackendJob> {
		return this.get(`v3/hireJobs/hygienist/public/${jobId}`);
	}

	async getHireJob(jobId: number): Promise<BackendJob> {
		const hireJob = await this.get('v3/hireJobs/' + jobId)
		return {
			...hireJob,
			position: mapJobTypeToPosition(hireJob.jobType)
		}
	}

	async getApplicationsForHireJob(jobId: number): Promise<Array<Application>> {
		const backendCandidates: Array<any> = await this.get(`v3/hireJobs/${jobId}/applications`)
		return backendCandidates
			.map(
				({
					 id: applicationId,
					 hygienistId,
					 status,
					 hygienist: {
						 graduationYear,
						 school,
						 starScore,
						 firstName,
						 lastName,
						 photoUrl,
						 isReliable,
						 ...hygienistProps
					 },
					 user: {
						 ...userProps
					 },
					 ...props
				 }) => {
					const yearsOfExperienceValue = dayjs().diff(`${graduationYear}-01-01`, 'years');

					return {
						...props,
						...hygienistProps,
						...userProps,
						applicationId,
						hygienistId,
						status,
						name: (firstName || lastName)
							? `${firstName} ${lastName}`
							: `JS${applicationId}${hygienistId}`,
						hygienistStatus: hygienistProps.status,
						school,
						graduationYear,
						photoUrl,
						graduation: `Graduated ${graduationYear} from ${school}`,
						yearsOfExperience: `${yearsOfExperienceValue} ${yearsOfExperienceValue === 1 ? 'year' : 'years'} experience`,
						rating: starScore,
						id: undefined,
					}
				})
	}

	getJobDetails(jobId: number): Promise<any> {
		return this.get(`v3/jobs/${jobId}/details`)
	}

	getHygienistStatsById(props: { hygienistId: number, applicationId: number }): Promise<CandidateStats> {
		const url = `v3/hygienists/${props.hygienistId}/statsForJobBoard?applicationId=${props.applicationId}`;
		return this.get(url);
	}

	purchaseResume(applicationId: number): Promise<any> {
		return this.put(`v3/jobApplications/${applicationId}/viewResume`)
	}

	checkRecentlyViewed(applicationId: number): Promise<any> {
		return this.get(`v3/jobApplications/${applicationId}/checkRecentlyViewed`)
	}

	async updateApplication({ applicationId, newData }: { applicationId: number, newData: any }): Promise<any> {
		await this.put(`v3/jobApplications/${applicationId}`, newData)
		return newData
	}

	async getLocations(): Promise<Array<Location>> {
		let backendLocations: Array<any> = await this.get('v3/locations')
		backendLocations = backendLocations.filter(location => location.removed === 0);
		return backendLocations
			.map(
				({
					 id,
					 address,
					 card,
					 ...props
				 }) => ({
					...props,
					id,
					address,
					hasCard: !!card,
					card,
				}))
	}

	async getValidLocations(): Promise<Array<Location>> {
		let backendLocations: Array<any> = await this.get('v3/locations')
		backendLocations = backendLocations.filter(location => location.removed === 0
			|| location.isCardValid === 0
			|| location.deactivated === 1)
		return backendLocations
			.map(
				({
					 id,
					 address,
					 card,
					 ...props
				 }) => ({
					...props,
					id,
					address,
					hasCard: !!card,
					card,
				}))
	}

	async cardById(customerId: string, country: number): Promise<SavedCard | null> {
		const cards: {data: {last4: any, brand: string}[] | null} = await this.post(`v3/locations/cards`, {customerId, country})
		const defaultCard = cards?.data?.length && cards?.data[0] || null

		return defaultCard ? {
			last4: defaultCard.last4,
			type: defaultCard.brand.toLowerCase(),
			} : null
	}

	addLocation(location: NewLocation): Promise<object> {

		return this.post('v3/locations', {
			address: location.streetAddress,
			city: location.unit,
			email: location.email,
			practiceName: location.unit,
			province: location.province,
			postalCode: location.zipCode,
			country: 0, // CANADA!!!
		})
	}

	deleteLocation(locationId: number): Promise<object> {
		return this.delete(`v3/locations/${locationId}`)
	}

	updatePaymentInfo(locationId: number, cardData?: any): Promise<object> {
		return this.put(`v3/locations/${locationId}/paymentInfo`, cardData)
	}

	deleteJobPosting(jobPostingId: number) {
		return this.put(`v3/hireJobs/${jobPostingId}/cancelJob`)
	}

	purchaseTokens(
		{
			locationId,
			tokensAmount,
		}: { locationId: number, tokensAmount: number },
	): Promise<undefined> {
		return this.post(`v3/hireJobs/purchaseTokens`, {
			locationId,
			tokensAmount,
		})
	}

	createUser({ email, password, affiliateCode}: { email: string, password: string, affiliateCode: string}): Promise<{ userId: number }> {
		return this.post('v3/tsusers/createUser', { email, password, affiliateCode})
	}

	checkUniqueHygienistPhoneNumber({ phone, type }: { phone: number, type: number }) {
		return this.post('v3/hygienists/check/phoneNumber', { phone, type })
	}

	async checkUniqueHygienistCDHONumber({ CDHONumber, type, province }: { CDHONumber: number, type: number, province: string }) {
		return this.post('v3/hygienists/check/CDHONumber', { CDHONumber, type, province })
	}

	createUserRole({ roleId, userId }:  { roleId: number, userId: number }): Promise<{ roleMappingId: number }> {
		return this.post('v3/tsusers/createUserRole', { roleId, userId })
	}

	getUser(): Promise<UserAccountProps> {
		return this.get('v3/tsusers/me')
	}

	getRole(): Promise<{roleId: number}> {
		return this.get('v3/tsusers/me/role')
	}

	changeRole(role: number | null) {
		return this.put('v3/tsusers/me/changeRole', { roleId: role });
	}

	updateUser(data: any): Promise<any> {
		return this.put('v3/tsusers/me', data)
	}

	createDentist(data: any ): Promise<{ dentistId: number, locationId: number }> {
		return this.post('v3/dentists', data)
	}

	cancelHireJob(jobId: number): Promise<object> {
		return this.put(`v3/hireJobs/${jobId}/cancelJob`)
	}

	postJob(editedJob: EditedJob) {
		return this.post(`v3/hireJobs`, formatEditJobBackend(editedJob))
	}

	editJob(editedJob: EditedJob) {
		return this.put(`v3/hireJobs/${editedJob.id}`, formatEditJobBackend(editedJob))
	}

	deleteJob(jobId: number) {
		return this.delete(`v3/hireJobs/${jobId}`)
	}

	async deleteUser(data?: { deleteReason?: string, details?: string | null }): Promise<any>  {
		return this.post(`v3/tsusers/me/deactivate`, data)
	}

	async uploadPDF(file: File): Promise<string> {
		const { url, signedRequest } = await this.get('v3/aws/uploadCredentials')
		await fetch(signedRequest, {
			method: 'PUT',
			headers: {
				'Content-Type': file.type,
			},
			body: file,
		})
		await this.put(`v3/hygienists/me`, {
			hireResumeUrl: url,
		})
		return url
	}

	async applyForHireJob(hireJobId: number): Promise<any> {
		return this.post(`v3/jobApplications`, {
			hireJobId,
		})
	}

	async withDrawForHireJob({ applicationId }: { applicationId: number }): Promise<any> {
		return this.delete(`v3/jobApplications/${applicationId}`)
	}

	async allowShowInfo({ ApplicationId, allow }: { ApplicationId: number, allow: boolean }): Promise<any> {
		return this.post(`v3/hirejobs/allowSeeInfo`, { ApplicationId, allow})
	}

	async getAvailableJobs(type: number, date?: string): Promise<any> {
		let url = 'v3/jobs/hygienistAvailableJobs';
		if (date) {
			url += '?date=' + date + '&type=' + (type ? 1 : 0);
		} else {
			url += '?type=' + (type ? 1 : 0);
		}
		return this.get(url)
	}

	async setFavouriteDentist(dentistId: number, locationId: number): Promise<any> {
		return this.post(`v3/hygienists/favouriteDentist`, { locationId, dentistId })
	}

	async removeFavouriteDentistByLocationId(locationId: number): Promise<any> {
		return this.delete(`v3/hygienists/favouriteDentist/${locationId}`)
	}

	async createSetupIntent(locationId: number): Promise<any> {
		return this.post(`v3/locations/${locationId}/createSetupIntent`)
	}

	async getMarket({postalCode, name, state}: {postalCode?: string, name?: string, state?: string}): Promise<{ header: string, description: string }> {
		return this.post('v3/getMarket', {postalCode, name, state})
	}

	async createHygienist(data: any): Promise<any> {
		return this.post('v3/hygienists', data)
	}

	async getHygienist(hygienistId: number): Promise<any> {
		return this.get(`v3/hygienists/${hygienistId}`)
	}

	async updateHygienist(data: any): Promise<any> {
		return this.put('v3/hygienists/me', data)
	}

	async deleteHygienist(hygienistId: number): Promise<any> {
		return this.delete(`v3/hygienists/${hygienistId}`)
	}

	async updateHygienistAccount(data: any): Promise<any> {
		return this.put('v3/hygienists/me/updateAccount', data)
	}

	async updateHygienistSignup(data: any): Promise<any> {
		return this.put('v3/hygienists/updateSingUp', data)
	}

	async checkForDuplicateHygienist(hygienistId: number, firstName: string, lastName: string, userId: number, type: number, province: string): Promise<any> {
		return this.post(`v3/checkForDuplicateUser`, {hygienistId, firstName, lastName, userId, type, province})
	}

	marketingSms(data: {phoneNumber: string, platform: string, label: string}): Promise<any> {
		return this.post('v3/marketingSms', data)
	}

	login(email: string, password: string): Promise<{ token: string, isAgent?: boolean }> {
		return this.post('v3/tsusers/login', { email: email, password: password });
	}

	reLogin(): Promise<{ token: string, isAgent?: boolean }> {
		return this.post('v3/tsusers/me/reLogIn', {});
	}

	getSkills(data: {type: number, state: string}): Promise<HygienistSkill[]> {
		return this.post('v3/skills', data)
	}

	replaceHygienistSkills(skillIds: number[]): Promise<any> {
		return this.post('v3/skills/hygienist', { skillIds })
	}

	getHygienistSkills(hygienistId: number): Promise<any> {
		return this.get(`v3/skills/hygienist/${hygienistId}`)
	}

	checkTextContactInfo(message: string): Promise<{ spellChecked: string, enhanced: string }> {
		return this.post('v3/textFilter', { message })
	}

	checkJobDescriptionInfo(message: string): Promise<{ spellChecked: string, final: string }> {
		return this.post('v3/jobDescriptionFilter', { message })
	}

	getDentistJobs() {
		return this.get('v3/jobs/dentistJobs');
	}

	getLocationJobs(locationId: number) {
		return this.get(`v3/jobs/${locationId}/locationJobs`);
	}

	getLocationById(locationId: number) {
		return this.get(`v3/locations/getById?locationId=${locationId}`);
	}

	getLastJob({type, locationId}: {type: Type, locationId: number}): Promise<any> {
		return this.get(`v3/jobs/lastDentistJob?type=${type}&locationId=${locationId}`);
	}

	getAvgRate({type, postalCode}:{type: Type, postalCode: string}) {
		return this.get(`v3/jobs/averageRate?jobType=${type}&postalCode=${postalCode}`)
	}

	postRegularJob(data: JobDataProps) {
		return this.post(`v3/jobs/`, data)
	}

	getAvailableHygienists({jobId}: {jobId: number}) {
		return this.get('v3/jobs/available?jobId=' + jobId)
	}

	addWorkExperience(positions: PositionProps[]) {
		return this.post('v3/tsusers/me/workExperience', {...positions})
	}

	getWorkExperience(): Promise<PositionProps[]> {
		return this.get('v3/tsusers/me/workExperience')
	}

	getHygienistWorkExperience(hygienistId: number): Promise<PositionProps[]> {
		return this.get(`v3/hygienists/workExperience/${hygienistId}`)
	}

	makePartialOffer({hourlyRate, jobId, startTime, endTime, isPartial}: { hourlyRate: number, jobId: number, startTime?: string, endTime?: string, isPartial?: boolean }): Promise<any> {
		return this.post('v3/offers/', {hourlyRate, jobId, startTime, endTime, isPartial})
	}

	hideOffer({jobId}: { jobId: number }): Promise<any> {
		return this.post('v3/hideJob/' + jobId, {})
	}

	unHideOffer(): Promise<any> {
		return this.post('v3/unhideJobs/', {})
	}

	makeInstabookOffer(data: {
		hourlyRate: number,
		offeredStartTime: string,
		offeredEndTime: string,
		jobId: number,
		startDate: string,
		available: boolean
	}): Promise<{ offerId: number }> {
		return this.put('v3/jobs/instabook', data)
	}

	confirmInstabook(data: {offerId: number, jobId: number} ): Promise<any> {
		return this.post('v3/jobs/instabook', data)
	}

	revertInstabook(data: {offerId: number, jobId: number} ): Promise<any> {
		return this.delete('v3/jobs/instabook', data)
	}

	getDistanceMatrix(destination: {lat: number, lng: number} ): Promise<{distance: any, duration: any}> {
		return this.post('v3/locations/getDistanceMatrix', { destination })
	}

	completeJob(jobId: number): Promise<any> {
		return this.put(`v3/jobs/${jobId}/complete`)
	}

	resubmitOffer(offerId: number): Promise<any> {
		return this.put(`v3/offers/${offerId}/resubmit`)
	}

	checkFavouriteLocation(locationId: number): Promise<any> {
		return this.get(`v3/hygienists/isMyFavourite/${locationId}`)
	}

	checkFavouriteHygienist(data: {hygienistId: number, locationId: number}): Promise<any> {
		return this.get(`v3/dentists/isMyFavourite/${data.hygienistId}/${data.locationId}`)
	}

	checkFavouriteHygienistLocations(locationIds: number[]): Promise<any> {
		return this.post(`v3/hygienists/favouriteHygienistLocations`, { locationIds })
	}

	getResumeData(data: {hygienistId: number, locationId: number}): Promise<any> {
		return this.get(`v3/hygienists/resume/${data.hygienistId}/${data.locationId}`)
	}

	removePartialOffer(offerId: number): Promise<any> {
		return this.delete(`v3/offers/${offerId}`)
	}

	hygienistCancelJob(jobId: number): Promise<{ hygienistCancelledId: number }> {
		return this.post(`v3/jobs/${jobId}/cancel`, {})
	}

	dentistCancelJob(jobId: number): Promise<{dentistCancelledId: number}> {
		return this.delete(`v3/jobs/${jobId}`, {})
	}

	updateCancelled(data: {hygienistCancelledId: number, reason: string, details: string}): Promise<any> {
		return this.put(`v3/jobs/updateHygienistCancelled`, data)
	}

	updateDentistCancelled(data: {dentistCancelledId: number, reason: string, details: string, repostShift?: boolean}): Promise<any> {
		return this.put(`v3/jobs/updateDentistCancelled`, data)
	}

	async getAiAssistsLeft(): Promise<any> {
		return this.post('v3/textFilter/rewritesLeft')
	}

	getInvoiceForJob(jobId: number): Promise<InvoiceProps> {
		return this.get('v3/jobs/' + jobId + '/invoice');
	}

	payInvoice(invoiceId: number): Promise<any> {
		return this.post('v3/invoices/' + invoiceId + '/pay');
	}

	updateAlreadyPaidInvoice(invoiceId: number): Promise<any> {
		return this.post('v3/invoices/alreadyPaid', {invoiceId});
	}

	modifyJob({jobId, postedStart, postedEnd}: {jobId: number, postedStart: string, postedEnd: string }): Promise<any> {
		return this.put(`v3/jobs/${jobId}`, {postedStart, postedEnd})
	}

	getPartialOffer({offerId}: {offerId: number}): Promise<any> {
		return this.get(`v3/offers/${offerId}`)
	}

	updatePartialOfferViews({offerId}: { offerId: number }): Promise<any> {
		return this.post(`v3/offers/${offerId}/updateViews`);
	}
	
	acceptPartialOffer(offerId: number): Promise<any> {
		return this.put(`v3/offers/${offerId}/accept`);
	}
	
	rejectPartialOffer(offerId: number, data:object): Promise<any> {
		return this.put(`v3/offers/${offerId}/reject`, data);
	}
	
	cancelJob(jobId: number): Promise<any> {
		return this.put(`v3/jobs/${jobId}/reject`, {});
	}

	removeJob(jobId: number): Promise<any> {
		return this.delete(`v3/jobs/${jobId}`, {});
	}
	
	getHygienistJobs(filter: string): Promise<any> {
		return this.get(`v3/jobs/hygienistJobs${filter ? '?filter=' + filter : ''}`);
	}

	getHygienistOffers(): Promise<any> {
		return this.get(`v3/offers/activeForHygienist`);
	}

	getResumesExpirationDate(): Promise<string> {
		return this.get(`v3/resumesExpirationDate`);
	}
	
	requestResubmitOffer({offerId}: { offerId: number }): Promise<any> {
		return this.post(`v3/offers/${offerId}/requestResubmit`);
	}

	resetPassword(data: {password: string, token: string}): Promise<any> {
		return this.post('v3/tsusers/resetPassword', data);
	}

	contactSupport(data: any): Promise<any> {
		return this.post('v3/tsusers/support', data);
	}

	getCancellationStats({hygienistId}: {hygienistId: number}): Promise<BasicHygienistScoreProps> {
		return this.get(`v3/hygienists/cancellationStats/${hygienistId}`);
	}

	calculateCancellationStats({jobId}: {jobId: number}): Promise<HygienistScoreProps> {
		return this.post(`v3/hygienists/cancellationStats`, {jobId});
	}
	
	sendInvoice(data: any): Promise<any> {
		return this.post('v3/invoices', data);
	}

	getHygienistPersonalStats(): Promise<any> {
		return this.get('v3/hygienists/me/personalStats');
	}

	getHygienistStats(): Promise<any> {
		return this.get(`v3/hygienists/reliabilityScorePosition`);
  }
  
	resendInvoice(data: any): Promise<any> {
		return this.post(`v3/invoices/${data.id}/resend`, data);
	}

	updateInvoice(data: any): Promise<any> {
		return this.put(`v3/invoices/${data.id}`, data);
	}

	saveFeedback(data: any): Promise<any> {
		return this.post('v3/jobs/feedback', data);
	}
	
	negativeFeedback(data: any): Promise<any> {
		return this.post(`v3/jobs/${data.job.id}/negativeFeedback`, data)
	}
	
	positiveFeedback(data: any): Promise<any> {
		return this.post(`v3/jobs/${data.job.id}/positiveFeedback`, data)
	}

	// New talent office review methods
	submitTalentReview(reviewData: ReviewData): Promise<TalentReview> {
		return this.post('v3/hygienists/review', reviewData)
			.then(response => {
				return response.data;
			})
			.catch(error => {
				if (error.response) {
				}
				throw error;
			});
	}
	
	getTalentReview(jobId: number): Promise<TalentReview> {
		return this.get(`v3/hygienists/review/${jobId}`)
			.then(response => {
				return response.data;
			})
			.catch(error => {
				throw error;
			});
	}
	
	dismissTalentReview(jobId: number): Promise<any> {
		return this.post(`v3/hygienists/review/dismiss/${jobId}`)
			.then(response => {
				return response;
			})
			.catch(error => {
				throw error;
			});
	}
	
	async getPendingTalentReviews(): Promise<PendingReview[]> {
		try {
			const response = await this.get('v3/hygienists/pending-reviews');
			return response.data;
		} catch (error) {
			throw error;
		}
	}

}
